import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { resetPasswordSchema, type ResetPasswordInput } from '@cmms/validation'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import { AuthShell } from '../../layouts/AuthShell'
import { api } from '../../lib/api'
import { ApiError } from '@cmms/api-client'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const orgSlug = searchParams.get('org') ?? ''

  const [validating, setValidating] = useState(true)
  const [validEmail, setValidEmail] = useState<string | null>(null)
  const [invalidToken, setInvalidToken] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { org_slug: orgSlug, token, new_password: '', confirm_password: '' },
  })

  useEffect(() => {
    if (!token || !orgSlug) {
      setInvalidToken(true)
      setValidating(false)
      return
    }
    api.auth.validateResetToken(token, orgSlug)
      .then((res) => { setValidEmail(res.email); setValidating(false) })
      .catch(() => { setInvalidToken(true); setValidating(false) })
  }, [token, orgSlug])

  async function onSubmit(values: ResetPasswordInput) {
    setError(null)
    try {
      await api.auth.resetPassword(values)
      setDone(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to reset password')
    }
  }

  if (validating) {
    return (
      <AuthShell title="Verifying..." subtitle="Checking your reset link">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      </AuthShell>
    )
  }

  if (invalidToken) {
    return (
      <AuthShell title="Invalid link" subtitle="This password reset link is invalid or has expired.">
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          The reset link may have expired or already been used. Please request a new one.
        </Alert>
        <Button component={RouterLink} to="/forgot-password" variant="contained" fullWidth sx={{ borderRadius: 99, py: 1.25 }}>
          Request new link
        </Button>
      </AuthShell>
    )
  }

  if (done) {
    return (
      <AuthShell
        title={<Stack direction="row" alignItems="center" spacing={1}><CheckCircleOutlinedIcon sx={{ color: '#059669' }} /><span>Password reset</span></Stack>}
        subtitle="Your password has been updated successfully."
      >
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          You can now sign in with your new password.
        </Alert>
        <Button component={RouterLink} to="/login" variant="contained" fullWidth sx={{ borderRadius: 99, py: 1.25 }}>
          Go to login
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Set new password" subtitle={validEmail ? `For ${validEmail}` : 'Enter your new password below.'}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2.5}>
          <TextField
            label="New password"
            type={showPassword ? 'text' : 'password'}
            {...register('new_password')}
            error={!!errors.new_password}
            helperText={errors.new_password?.message}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm password"
            type={showConfirm ? 'text' : 'password'}
            {...register('confirm_password')}
            error={!!errors.confirm_password}
            helperText={errors.confirm_password?.message}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained" fullWidth disabled={isSubmitting}
            sx={{ borderRadius: 99, py: 1.25, fontWeight: 600 }}>
            {isSubmitting ? 'Resetting...' : 'Reset password'}
          </Button>
        </Stack>
      </form>

      <Typography sx={{ mt: 3, textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>
        Remember your password?{' '}
        <Link component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
          Sign in
        </Link>
      </Typography>
    </AuthShell>
  )
}
